import {Component, computed, effect, ElementRef, inject, OnDestroy, viewChild} from '@angular/core';
import {TaListBase} from '../../../pages/ta-list-base.directive';
import {FormsModule} from '@angular/forms';
import {TableModule} from 'primeng/table';
import {Button} from 'primeng/button';
import {DatePipe, NgClass} from '@angular/common';
import {Chip} from 'primeng/chip';
import {InputText} from 'primeng/inputtext';
import {toTAStatusText} from '../../../../util/app.types';
import {LocationService} from '../../../../services/location.service';
import {ScrollHeightDirective} from '../../../directives/scroll-height.directive';
import {PrepaidIndicatorComponent} from '../../prepaid-indicator/prepaid-indicator.component';
import {AttachmentIndicatorComponent} from '../../attachment-indicator/attachment-indicator.component';
import {TaCostBubbleComponent} from '../../ta-cost-bubble/ta-cost-bubble.component';
import {TaCommentCount} from '../../ta-comments/ta-comment-count';

@Component({
  selector: 'approval-history-list',
  imports: [
    TaCommentCount,
    Button,
    Chip,
    DatePipe,
    FormsModule,
    InputText,
    NgClass,
    TableModule,
    ScrollHeightDirective,
    PrepaidIndicatorComponent,
    AttachmentIndicatorComponent,
    TaCostBubbleComponent,
  ],
  templateUrl: './approval-history-list.component.html',
  styleUrl: './approval-history-list.component.scss'
})
export class ApprovalHistoryListComponent extends TaListBase implements OnDestroy {
  private scrollContainer = viewChild<ElementRef>('scrollContainer');
  private loadMoreSentinel = viewChild<ElementRef>('loadMoreSentinel');
  private observer: IntersectionObserver | undefined;
  private loadingDebounceTimer: ReturnType<typeof setTimeout> | undefined;

  protected locationService = inject(LocationService);

  isLoading = computed(() => this.store.isLoadingData());
  canLoadMore = computed(() => {
    const cache = this.store.approvalHistoryTaListCache();
    if (!cache?.totalCount) return false;
    return (cache.currentCount ?? 0) < cache.totalCount;
  });

  constructor() {
    super();

    effect(() => {
      this.availableTaList.set(this.store.approvalHistoryTaListCache()?.taList ?? []);
    });

    effect(() => {
      const el = this.loadMoreSentinel()?.nativeElement;
      const root = this.scrollContainer()?.nativeElement;
      this.observer?.disconnect();
      if (el && root) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              this.loadMore();
            }
          },
          { root, threshold: 0.1 },
        );
        this.observer.observe(el);
      }
    });

    effect(() => {
      const loading = this.isLoading();
      if (loading) {
        this.loadingDebounceTimer = setTimeout(() => {
          this.loadingService.showLoading({message: 'Loading...'});
        }, 80);
      } else {
        clearTimeout(this.loadingDebounceTimer);
        this.loadingDebounceTimer = undefined;
        this.loadingService.hideLoading();
      }
    });
  }

  // The loader decides whether to refetch - it reuses a cache newer than LIST_CACHE_TTL_MS.
  override ngOnInit(): void {
    super.ngOnInit();
    this.taSearchDatasource.loadApprovalHistoryTaList();
    this.locationService.loadLocations();
  }

  // The explicit refresh button, which must always hit the server.
  reloadTaList(): void {
    this.store.resetApprovalHistoryCache();
    this.taSearchDatasource.loadApprovalHistoryTaList();
  }

  loadMore(): void {
    if (!this.canLoadMore() || this.isLoading()) return;
    this.taSearchDatasource.loadMoreApprovalHistoryTas();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    clearTimeout(this.loadingDebounceTimer);
    this.observer?.disconnect();
  }

  protected readonly toTAStatusText = toTAStatusText;
}
